// Risk threshold bands: reading them, writing them, and checking they make sense.
//
// A category is scored by three bands — Good, Moderate, Bad — and every scan in the
// workspace is graded against them. That is what makes this screen worth being careful
// about: a band with a gap in it silently produces findings that match no tier, and a
// band that overlaps grades the same number two different ways depending on which tier
// is tested first.
//
// The rules come back from the API in the shape the v1 editor wrote them:
//   { operator: "<=", value: 20 }
//   { operator: "Between", lower: 21, upper: 50 }
// with an optional `note` and a `notes` library alongside.

export const TIERS = ["Good", "Moderate", "Bad"];

export const TIER_TONE = { Good: "success", Moderate: "warning", Bad: "danger" };

export const OPERATORS = [
  { value: "<",       label: "Less than" },
  { value: "<=",      label: "At most" },
  { value: ">",       label: "More than" },
  { value: ">=",      label: "At least" },
  { value: "==",      label: "Exactly" },
  { value: "Between", label: "Between" },
];

const SYMBOL = { "<": "<", "<=": "≤", ">": ">", ">=": "≥", "==": "=" };

export const isBetween = (rule) => rule?.operator === "Between";

const num = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

// Plain English, because "≤ 20" in a table of seventy rows is read far more often than
// it is edited, and the operator symbol alone does not say what is being counted.
export function describeRule(rule) {
  if (!rule || !rule.operator) return "Not set";
  if (isBetween(rule)) {
    const lo = num(rule.lower);
    const hi = num(rule.upper);
    if (lo === null || hi === null) return "Not set";
    return `${lo} to ${hi}`;
  }
  const v = num(rule.value);
  if (v === null) return "Not set";
  return `${SYMBOL[rule.operator] || rule.operator} ${v}`;
}

// The inclusive integer span a rule actually matches. Every threshold in this product is
// a whole number — a count of devices or a percentage — so strict operators are widened
// by one rather than carrying open/closed interval bookkeeping that nothing else needs.
export function spanOf(rule) {
  if (!rule || !rule.operator) return null;
  if (isBetween(rule)) {
    const lo = num(rule.lower);
    const hi = num(rule.upper);
    if (lo === null || hi === null) return null;
    return [lo, hi];
  }
  const v = num(rule.value);
  if (v === null) return null;
  switch (rule.operator) {
    case "<":  return [-Infinity, v - 1];
    case "<=": return [-Infinity, v];
    case ">":  return [v + 1, Infinity];
    case ">=": return [v, Infinity];
    // The backend evaluates "==" too (utils/thresholds.js), and several shipped
    // categories use it for a single-value band such as "exactly one EOL machine".
    case "==": return [v, v];
    default:   return null;
  }
}

const fmtBound = (n) => (n === -Infinity ? "anything below" : n === Infinity ? "anything above" : n);

// Advisory, never blocking. These are judgement calls about scoring policy, and a
// consultant may genuinely want a gap — refusing to save would be this screen deciding
// it knows the client's grading better than they do.
export function checkBands(rules) {
  const problems = [];

  const spans = TIERS.map((tier) => {
    const rule = rules?.[tier];
    const span = spanOf(rule);
    if (!span) {
      problems.push({ tier, text: `${tier} has no value set, so nothing will ever match it.` });
    } else if (span[0] > span[1]) {
      problems.push({
        tier,
        text: `${tier} spans ${fmtBound(span[0])} to ${fmtBound(span[1])}, which matches nothing. Check the lower and upper values.`,
      });
      return null;
    }
    return span ? { tier, span } : null;
  }).filter(Boolean);

  const ordered = [...spans].sort((a, b) => a.span[0] - b.span[0]);

  for (let i = 0; i < ordered.length - 1; i += 1) {
    const a = ordered[i];
    const b = ordered[i + 1];
    if (b.span[0] <= a.span[1]) {
      problems.push({
        tier: b.tier,
        text: `${a.tier} and ${b.tier} overlap between ${b.span[0]} and ${Math.min(a.span[1], b.span[1])}. A value in that range could be graded either way.`,
      });
    } else if (b.span[0] > a.span[1] + 1) {
      problems.push({
        tier: b.tier,
        text: `Nothing covers ${a.span[1] + 1} to ${b.span[0] - 1}. A result in that range would match no band at all.`,
      });
    }
  }

  return problems;
}

// Strip a draft back to what the API stores. Blank numeric boxes become null rather than
// 0: "not recorded" and "zero" grade very differently.
export function toPayload(rules) {
  const out = {};
  TIERS.forEach((tier) => {
    const r = rules?.[tier] || {};
    const base = { operator: r.operator };
    if (isBetween(r)) {
      base.lower = num(r.lower);
      base.upper = num(r.upper);
    } else {
      base.value = num(r.value);
    }
    // Carried through untouched: the note and its library belong to the tenant and this
    // screen does not edit them, so writing back a trimmed copy would quietly drop them.
    if (r.note !== undefined) base.note = r.note;
    if (r.notes !== undefined) base.notes = r.notes;
    out[tier] = base;
  });
  return out;
}

// True when the draft says the same thing as what is stored, so Save can stay disabled
// rather than writing an override identical to the default.
export function sameRules(a, b) {
  return TIERS.every((tier) => {
    const x = spanOf(a?.[tier]);
    const y = spanOf(b?.[tier]);
    if (!x || !y) return x === y;
    return x[0] === y[0] && x[1] === y[1];
  });
}
